import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function UpgradeModal({ open, onOpenChange, quota }) {
  const { user } = useAuth();
  const [email, setEmail] = useState(user?.email || "");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/waitlist", { email, reason: `Free limit hit (${quota?.used}/${quota?.limit})` });
      setDone(true);
      toast.success("You're on the Premium waitlist.");
    } catch {
      toast.error("Could not save. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="upgrade-modal">
        <DialogHeader>
          <Badge className="w-fit bg-orange-600 hover:bg-orange-600 text-white font-mono text-[10px] uppercase tracking-[0.2em]">
            Coming soon
          </Badge>
          <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
            You've used {quota?.used || 7} of {quota?.limit || 7} free exams
          </DialogTitle>
          <DialogDescription className="text-stone-600">
            Premium unlocks unlimited uploads, AI error categorization, exam replay, and unlimited history.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-3 border-t border-b border-stone-200">
          {[
            "Unlimited exam uploads per month",
            "AI mistake categorization on every attempt",
            "Exam replay timeline & full history",
            "Priority AI parsing queue",
          ].map((f) => (
            <div key={f} className="flex items-center gap-3 text-sm">
              <ShieldCheck className="h-4 w-4 text-emerald-600 flex-shrink-0" />
              {f}
            </div>
          ))}
        </div>

        {done ? (
          <div className="text-center py-4">
            <Sparkles className="h-10 w-10 mx-auto text-orange-600 mb-2" />
            <div className="font-heading text-lg font-semibold">You're on the list</div>
            <p className="text-sm text-stone-500 mt-1">We'll email you 50% off for life at launch.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              data-testid="upgrade-email-input"
            />
            <Button
              type="submit"
              disabled={busy}
              className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
              data-testid="upgrade-waitlist-submit"
            >
              Join Premium waitlist
            </Button>
            <p className="text-xs text-stone-500 text-center">
              Get 50% off for life when Premium launches.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
